/**
 * The owner's Note key (v26.10.027, a temporary test aid; shared/feedback.ts): a picture of this
 * screen as it is, and the posts that carry a note and the wall's answer to the server.
 */

import { toJpeg } from "html-to-image";

/** A random id for one note, which the wall's picture is joined to it by. */
export function noteId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** The server's address for a path under /api, on this tournament. */
export function apiUrl(path: string, tournament: string): string {
  return `${path}?${new URLSearchParams({ t: tournament }).toString()}`;
}

/**
 * A JPEG of `el` as it is now, under `maxBytes`: tried at quality 0.7, then smaller and plainer
 * until it fits. `viewport`: only the part of the page on the screen (the page is moved up by
 * what has been scrolled, so what is fixed to the screen stays where it is). Null when it cannot
 * be taken; a note goes without it.
 */
export async function snapJpeg(el: HTMLElement, maxBytes: number, viewport = false): Promise<Blob | null> {
  const frame = viewport ? { width: window.innerWidth, height: window.innerHeight, style: { marginTop: `${-window.scrollY}px`, height: `${window.scrollY + window.innerHeight}px`, overflow: "hidden" } } : {};
  const tries: { quality: number; pixelRatio: number }[] = [
    { quality: 0.7, pixelRatio: 1 },
    { quality: 0.5, pixelRatio: 1 },
    { quality: 0.5, pixelRatio: 0.6 },
    { quality: 0.4, pixelRatio: 0.4 },
  ];
  for (const opt of tries) {
    try {
      const url = await toJpeg(el, { ...opt, ...frame, backgroundColor: getComputedStyle(document.body).backgroundColor || "#ffffff", cacheBust: false });
      const blob = await (await fetch(url)).blob();
      if (blob.size <= maxBytes) return blob;
    } catch {
      return null;
    }
  }
  return null;
}

/** Post the owner's note. Resolves to the note's number, or null when it was not saved. */
export async function postNote(tournament: string, session: string | null, note: { text: string; rid: string; screen: string; phone: Blob | null }): Promise<number | null> {
  if (!session) return null;
  const form = new FormData();
  form.append("text", note.text);
  form.append("rid", note.rid);
  form.append("screen", note.screen);
  if (note.phone) form.append("phone", note.phone, "phone.jpg");
  try {
    const res = await fetch(apiUrl("/api/feedback", tournament), { method: "POST", body: form, headers: { "x-bd-session": session } });
    const data = (await res.json().catch(() => null)) as { ok?: boolean; id?: number } | null;
    return res.ok && data?.ok && typeof data.id === "number" ? data.id : null;
  } catch {
    return null;
  }
}

/** The wall's answer: what it shows, and its picture when it could take one. */
export async function postWall(tournament: string, link: string, rid: string, text: string, wall: Blob | null): Promise<boolean> {
  const form = new FormData();
  form.append("text", text);
  if (wall) form.append("wall", wall, "wall.jpg");
  try {
    const res = await fetch(apiUrl(`/api/feedback/wall/${encodeURIComponent(rid)}`, tournament), { method: "POST", body: form, headers: { "x-bd-link": link } });
    return res.ok;
  } catch {
    return false;
  }
}
